import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Ticket } from "lucide-react";
import { Alert, AlertDescription, Button, Field, Input } from "@pracownik/ui";
import { signupSchema, type SignupInput } from "@pracownik/shared/schemas";
import { configApi } from "@pracownik/sdk-web";
import { signUp, signIn } from "@/lib/auth-client";
import { brand } from "@/lib/brand";
import { INVITE_ERRORS, SOCIAL_FAILED_KEY, useOAuthErrorKey } from "@/lib/oauth-error";
import { AuthLayout } from "@/layouts/auth-layout";
import { GoogleIcon } from "@/components/google-icon";

export function SignupPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // A failed Google round-trip lands back here as ?error=<CODE> (see handleGoogle).
  const oauthKey = useOAuthErrorKey();
  const [inviteError, setInviteError] = useState<string | null>(() =>
    oauthKey && oauthKey !== SOCIAL_FAILED_KEY ? t(oauthKey) : null,
  );

  const { data: beta } = useQuery({
    queryKey: ["config", "beta"],
    queryFn: () => configApi.getBeta(),
    staleTime: 10 * 60 * 1000,
  });
  const inviteRequired = beta?.inviteRequired ?? true;

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      inviteCode: params.get("invite")?.toUpperCase() ?? "",
    },
  });

  const onSubmit = async (data: SignupInput) => {
    setLoading(true);
    setInviteError(null);
    try {
      const result = await signUp.email({
        name: data.name,
        email: data.email,
        password: data.password,
        // Domain decides language for the whole product; persisting it on the
        // user keeps every transactional email in the brand they signed up from.
        locale: brand.locale,
        inviteCode: data.inviteCode?.trim() || undefined,
      } as Parameters<typeof signUp.email>[0]);
      if (result.error) {
        const code =
          (result.error as { code?: string; message?: string }).code ?? result.error.message ?? "";
        if (INVITE_ERRORS.has(code)) {
          setInviteError(t(`invite.errors.${code}`));
          form.setFocus("inviteCode");
          return;
        }
        if (code === "USER_ALREADY_EXISTS") {
          toast.error(t("emailTaken"));
          return;
        }
        toast.error(t("signupFailed"));
        return;
      }
      navigate(`/verify-email-sent?email=${encodeURIComponent(data.email)}`);
    } catch {
      toast.error(t("signupFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}/dashboard`,
        // Without it BetterAuth strands the user on its raw API error page.
        errorCallbackURL: `${window.location.origin}/signup`,
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t("signUp")}
      description={inviteRequired ? t("signupDescriptionBeta") : t("signupDescription")}
    >
      {oauthKey === SOCIAL_FAILED_KEY && (
        <Alert variant="destructive" className="mb-5">
          <AlertDescription>{t(SOCIAL_FAILED_KEY)}</AlertDescription>
        </Alert>
      )}

      {inviteRequired && (
        <Alert variant="info" className="mb-5">
          <Ticket />
          <AlertDescription>
            {t("invite.betaNotice")}{" "}
            <a href={brand.requestAccessUrl} className="font-medium underline underline-offset-2">
              {t("invite.requestAccess")}
            </a>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {inviteRequired && (
          <Field
            htmlFor="inviteCode"
            label={t("invite.label")}
            hint={t("invite.hint")}
            error={
              inviteError ?? (form.formState.errors.inviteCode && t("invite.errors.INVITE_INVALID"))
            }
          >
            <Input
              id="inviteCode"
              autoComplete="one-time-code"
              placeholder="BETA-XXXX-XXXX"
              className="font-mono uppercase tracking-wider"
              {...form.register("inviteCode", { onChange: () => setInviteError(null) })}
            />
          </Field>
        )}
        <Field
          htmlFor="name"
          label={t("name")}
          error={form.formState.errors.name && t("nameTooShort")}
        >
          <Input
            id="name"
            autoComplete="name"
            placeholder={t("namePlaceholder")}
            {...form.register("name")}
          />
        </Field>
        <Field
          htmlFor="email"
          label={t("email")}
          error={form.formState.errors.email && t("invalidEmail")}
        >
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@example.com"
            {...form.register("email")}
          />
        </Field>
        <Field
          htmlFor="password"
          label={t("password")}
          hint={t("passwordPlaceholder")}
          error={form.formState.errors.password && t("passwordTooShort")}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
          />
        </Field>

        <Button type="submit" className="w-full" size="lg" loading={loading}>
          {t("createAccount")}
        </Button>

        <p className="text-center text-xs leading-relaxed text-text-tertiary">
          {t("agreeToTerms")}{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
            {t("termsLink")}
          </Link>{" "}
          {t("and")}{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
            {t("privacyLink")}
          </Link>
          .
        </p>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card px-3 text-xs text-text-tertiary">{t("orContinueWith")}</span>
        </div>
      </div>

      <Button
        variant="outline"
        size="lg"
        className="w-full"
        onClick={handleGoogle}
        loading={googleLoading}
      >
        <GoogleIcon className="size-4" />
        {t("continueWithGoogle")}
      </Button>
      {inviteRequired && (
        <p className="mt-2 text-center text-xs text-text-tertiary">{t("invite.googleHint")}</p>
      )}

      <p className="mt-6 text-center text-sm text-text-secondary">
        {t("hasAccount")}{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          {t("login")}
        </Link>
      </p>
    </AuthLayout>
  );
}
